import { Link, useNavigate, useRouterState } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";

import type { NavSection } from "~/lib/utils/navigation";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "./ui/collapsible";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "./ui/sidebar";

export function NavMain({ sections }: { sections: NavSection[] }) {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const navigate = useNavigate();

  return (
    <>
      {sections.map((section, sectionIndex) => {
        // Collapsible sections (Finances, Settings)
        if (section.collapsible) {
          const Icon = section.icon;

          // When sidebar is collapsed, show dropdown menu instead
          if (isCollapsed) {
            return (
              <SidebarGroup
                className="py-0"
                key={section.label ?? sectionIndex}
              >
                <SidebarMenu>
                  <SidebarMenuItem>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <SidebarMenuButton
                          isActive={section.isActive}
                          tooltip={section.label}
                        >
                          {Icon && <Icon />}
                        </SidebarMenuButton>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="start"
                        className="w-48"
                        side="right"
                      >
                        {section.items.map((item) => (
                          <DropdownMenuItem
                            key={item.title}
                            onClick={() => navigate({ to: item.url })}
                          >
                            {item.title}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </SidebarMenuItem>
                </SidebarMenu>
              </SidebarGroup>
            );
          }

          // Expanded sidebar - show collapsible with sub-items
          return (
            <SidebarGroup className="py-0" key={section.label ?? sectionIndex}>
              <SidebarMenu>
                <Collapsible
                  asChild
                  className="group/collapsible"
                  defaultOpen={section.isActive}
                >
                  <SidebarMenuItem>
                    <CollapsibleTrigger asChild>
                      <SidebarMenuButton
                        isActive={section.isActive}
                        tooltip={section.label}
                      >
                        {Icon && <Icon />}
                        <span>{section.label}</span>
                        <ChevronRight className="ml-auto transition-transform duration-200 group-data-[expanded]/collapsible:rotate-90" />
                      </SidebarMenuButton>
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                      <SidebarMenuSub>
                        {section.items.map((item) => (
                          <SidebarMenuSubItem key={item.title}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={item.isActive}
                            >
                              <Link to={item.url}>
                                <span>{item.title}</span>
                              </Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        ))}
                      </SidebarMenuSub>
                    </CollapsibleContent>
                  </SidebarMenuItem>
                </Collapsible>
              </SidebarMenu>
            </SidebarGroup>
          );
        }

        // Non-collapsible sections (Locations)
        return (
          <SidebarGroup key={section.label ?? sectionIndex}>
            <SidebarMenu>
              {section.items.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton
                    asChild
                    isActive={item.isActive}
                    tooltip={item.title}
                  >
                    <Link to={item.url}>
                      {item.icon && <item.icon />}
                      <span>{item.title}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroup>
        );
      })}
    </>
  );
}

export function useActiveRoute() {
  const router = useRouterState();
  return router.location.pathname;
}
